import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { BuyerPhoneConfig } from "@baluu/ui/components/checkout";
import type { CheckoutCustomerField } from "@baluu/ui/components/checkout/customer-fields";
import {
  CUSTOMER_FIELDS_FETCH_TIMEOUT_MS,
  linkedAnswersFromSelfAttendees,
  readCustomerFieldsResponse,
  seedSavedCustomerFieldValues,
  type CustomerFieldsStatus,
} from "@baluu/ui/lib/checkout/customer-field-answers";
import type { BasketItem } from "~/widgets/stores/basket-store";
import { apiOrigin } from "~/widgets/lib/basket-validate";

/** One empty list, so "no fields yet" is the same value every render. */
const NO_FIELDS: CheckoutCustomerField[] = [];

type Params = {
  apiUrl: string;
  businessId: string;
  items: BasketItem[];
  isLoggedIn: boolean;
  /** The signed-in customer's bearer token, for their saved answers. */
  authToken: string | null | undefined;
};

/**
 * The school's checkout customer fields ("Your information") for this
 * basket, as the widget's full-page checkout has them
 * (apps/widget/.../useCheckoutCustomerFields.ts): the field definitions for
 * the basket's courses, the answers map, the built-in buyer-phone row, a
 * signed-in customer's saved answers, the answers already given on the
 * buyer's own attendee card, and the sign-out reset. The rules (payload
 * shape, required checks, seeding) are the shared ones in
 * `@baluu/ui/lib/checkout/customer-field-answers`.
 *
 * Not here: the widget's returning-customer recognition ("welcome back",
 * sign in by code), which this overlay has no flow for.
 */
export function useBasketCustomerFields({
  apiUrl,
  businessId,
  items,
  isLoggedIn,
  authToken,
}: Params) {
  // What the buyer typed (and what their saved profile filled in).
  const [typedAnswers, setTypedAnswers] = useState<Record<string, string>>({});
  // Whether the buyer is asked for a phone, and whether it's required, is the
  // school's built-in `phone` row, not a hardcode. The defaults are what this
  // overlay did before the row went live, so a stale API can't drop the field.
  const [buyerPhoneConfig, setBuyerPhoneConfig] = useState<BuyerPhoneConfig>({
    askAtCheckout: true,
    isRequired: true,
  });

  const updateCustomerFieldAnswer = useCallback((slug: string, value: string) => {
    setTypedAnswers((prev) => ({ ...prev, [slug]: value }));
  }, []);

  // The basket's distinct courses: the fields are the union every one of
  // them asks at checkout. Gift cards and products carry none, and a
  // course-less basket still asks the school's scope-"all" fields.
  const basketCourseIdsKey = useMemo(
    () =>
      [...new Set(items.map((item) => item.courseId).filter((id): id is string => !!id))].join(","),
    [items],
  );

  // Each answer is kept with the request it answers, so a new request (the
  // basket's courses changed) reads as "idle" until its own answer lands,
  // while the previous fields stay on screen rather than flickering away.
  const fieldsKey = `${apiUrl}|${businessId}|${basketCourseIdsKey}`;
  const [fetched, setFetched] = useState<{
    key: string;
    fields: CheckoutCustomerField[];
    status: Exclude<CustomerFieldsStatus, "idle">;
  } | null>(null);
  const customerFields = fetched?.fields ?? NO_FIELDS;
  const customerFieldsStatus: CustomerFieldsStatus =
    fetched?.key === fieldsKey ? fetched.status : "idle";

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    // Bounded, so a hung request resolves to "failed" (which never blocks
    // payment) instead of holding submit on "idle".
    const timer = setTimeout(() => controller.abort(), CUSTOMER_FIELDS_FETCH_TIMEOUT_MS);
    const params = new URLSearchParams({ schoolId: businessId, courseIds: basketCourseIdsKey });
    fetch(`${apiOrigin(apiUrl)}/widget/customer-fields?${params.toString()}`, {
      signal: controller.signal,
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((body) => {
        if (cancelled) return;
        const { fields, buyerPhone } = readCustomerFieldsResponse<BuyerPhoneConfig>(body);
        if (buyerPhone) setBuyerPhoneConfig(buyerPhone);
        setFetched({ key: fieldsKey, fields, status: "loaded" });
      })
      .catch(() => {
        if (cancelled) return;
        setFetched({ key: fieldsKey, fields: [], status: "failed" });
      })
      .finally(() => clearTimeout(timer));
    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [apiUrl, businessId, basketCourseIdsKey, fieldsKey]);

  // A signed-in customer's saved answers, into the fields they haven't typed
  // in. Best-effort: a failed lookup just leaves the fields to fill.
  useEffect(() => {
    if (!isLoggedIn || !authToken || customerFields.length === 0) return;
    let cancelled = false;
    fetch(
      `${apiOrigin(apiUrl)}/widget/customer-fields/values?schoolId=${encodeURIComponent(businessId)}`,
      { headers: { Authorization: `Bearer ${authToken}` } },
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => {
        if (cancelled) return;
        const values = (body?.values || {}) as Record<string, unknown>;
        setTypedAnswers((prev) => seedSavedCustomerFieldValues(prev, values, customerFields));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, authToken, customerFields, apiUrl, businessId]);

  // Answered already on the buyer's own attendee card: shown back rather
  // than asked again, and always part of the answers sent (they win over
  // anything typed, as in the widget, where the buyer can't see the field).
  const linkedFromAttendee = useMemo(
    () => linkedAnswersFromSelfAttendees(items, customerFields),
    [items, customerFields],
  );
  const customerFieldAnswers = useMemo(
    () => ({ ...typedAnswers, ...linkedFromAttendee }),
    [typedAnswers, linkedFromAttendee],
  );

  // Signed out (here or in another tab): answers seeded from that account
  // must not ride along under whatever email is typed next.
  const wasLoggedInRef = useRef(isLoggedIn);
  useEffect(() => {
    const wasLoggedIn = wasLoggedInRef.current;
    wasLoggedInRef.current = isLoggedIn;
    if (wasLoggedIn && !isLoggedIn) setTypedAnswers({});
  }, [isLoggedIn]);

  return {
    customerFields,
    customerFieldAnswers,
    customerFieldsStatus,
    updateCustomerFieldAnswer,
    buyerPhoneConfig,
    linkedFromAttendee,
  };
}
